/**
 * Translate an axios error from an "AI auto-fill" endpoint into a
 * founder-readable string.
 *
 * Used by Onboarding (pitch deck), FunderMandate (thesis), and
 * AdminBusinessNew (admin-side pitch ingest). Centralised so all three
 * flows handle Cloudflare timeouts, oversized files, and missing-detail
 * responses consistently — rather than each one falling back to the
 * unhelpful "Couldn't parse" message.
 *
 * @param {unknown} e         axios error
 * @param {object}  opts
 * @param {string}  opts.kind "pitch" | "thesis" — used for copy
 * @returns {string} a friendly error message safe to render in UI
 */
export function aiAutofillErrorMessage(e, { kind = "pitch" } = {}) {
  const noun = kind === "thesis" ? "thesis" : "pitch";
  const code = e?.code;
  const status = e?.response?.status;
  const detail = e?.response?.data?.detail;

  if (code === "ECONNABORTED" || status === 504 || status === 524) {
    return `The AI took too long to read your ${noun}. Try a shorter PDF (under 10 pages) or paste a brief description below.`;
  }
  if (typeof detail === "string" && detail.length > 0) {
    return detail;
  }
  if (Array.isArray(detail)) {
    const joined = detail.map(d => d?.msg).filter(Boolean).join(" · ");
    if (joined) return joined;
  }
  if (status === 413) {
    return "Your PDF is over the 15 MB limit. Try a slimmer export or paste your text below.";
  }
  if (status === 503) {
    return "AI is temporarily unavailable. Please retry in a minute, or paste your text below.";
  }
  if (!e?.response) {
    return "We couldn't reach the server. Check your connection and try again.";
  }
  return `Couldn't parse the ${noun}. Try a text-based PDF, or paste a short description below.`;
}
